<template>
  <div class="d-flex w-100 flex-grow-1 bg-dark">
    <SlideNavigationList :navigation-disabled="navigationDisabled" :put-go-to-slide="putGoToSlide"
      :put-stop-live="putStopLive" :active-slide="activeSlide" :slides="slides"/>

    <div class="d-flex flex-column flex-grow-1" style="width: 1px">
      <div v-if="!election.readyForVoting" class="text-center p-3 bg-warning">
        <h5>{{ $t("js.backend.live.ceremony_unfinished.header") }}</h5>

        <p class="mb-0">{{ $t("js.backend.live.ceremony_unfinished.body") }}</p>
      </div>
      <div v-if="activeSlide" class="d-flex flex-column flex-grow-1 overflow-auto" style="height:0; min-height: 350px">
        <SlideShow v-if="election" :election="election" :showVoterCounts="false" class="flex-grow-1 pretty-scroll-theme" style="height: 100%; width: unset; overflow: visible !important; overflow-y: auto" :showLocaleSwitcher="false"></SlideShow>
        <SlideShowControls
          :is-ballot="isBallot || null"
          :navigation-disabled="navigationDisabled"
          key="slideshow-controls"/>
      </div>
      <div v-else class="d-flex flex-column justify-content-center flex-grow-1" style="height:0; min-height: 400px; background: rgba(0,0,0,0.5)">
        <div class="text-center text-white p-2">
          <h3>{{ $t("js.slide_show.no_slide_active") }}</h3>
        </div>
      </div>
      <VotingRoundsSheet />
    </div>

    <ResetModal @resetBallot="putResetBallot" id="resetOuter"/>

    <ToastManager style="z-index: 5; position: absolute; left: 0; right: 0; top: 0; bottom: 0" placement="center center" class="mb-3 ms-3"/>
  </div>
</template>

<script lang="ts">
import { mapState } from "pinia";
import { defineComponent } from "vue";
import apiClient from "@/entrypoints/frontend/apiClient";
import SlideShow from "@/components/frontend/observer/SlideShow.vue";
import ResetModal from "./ResetModal.vue";
import ToastManager from "@/components/shared/toasts/ToastManager.vue";
import SlideNavigationList from "./live/SlideNavigationList.vue";
import VotingRoundsSheet from "./live/VotingRoundsSheet.vue";
import SlideShowControls from "./live/SlideShowControls.vue";
import { useSharedStore } from "@/entrypoints/stores/shared";
import { usePresentationStore } from "@/entrypoints/stores/presentation";
import type { Slide, LiveContestState } from "@/types";

export default defineComponent({
  name: "PresentationControl",
  components: {
    SlideShowControls,
    VotingRoundsSheet,
    SlideNavigationList,
    ToastManager,
    ResetModal,
    SlideShow,
  },
  computed: {
    ...mapState(useSharedStore, ["election"]),
    ...mapState(usePresentationStore, ["slides", "readonly", "activeSlide"]),
    navigationDisabled() {
      return (this.isBallot && this.ballotStateIn(["open", "countdown"])) || this.readonly;
    },
    nextSlide() {
      return this.slides.filter((s: Slide) => s.position === this.activeSlide.position + 1)[0];
    },
    previousSlide() {
      return this.slides.filter((s: Slide) => s.position === this.activeSlide.position - 1)[0];
    },
    isBallot() {
      return this.activeSlide && this.activeSlide.type === "Slides::BallotSlide";
    },
  },
  methods:{
    ballotStateIn(ballotStates: LiveContestState | Array<LiveContestState>) {
      if (this.isBallot) {
        if (Array.isArray(ballotStates)) {
          return ballotStates.includes(this.activeSlide.state);
        } else {
          return ballotStates === this.activeSlide.state;
        }
      }
      return false;
    },
    async putGoToNext() {
      if (this.nextSlide) {
        await this.putGoToSlide(this.nextSlide.id);
      }
    },
    async putGoToPrevious() {
      if (this.previousSlide) {
        await this.putGoToSlide(this.previousSlide.id);
      }
    },
    async putGoToSlide(id: number) {
      if ((this.activeSlide && id === this.activeSlide.id) || this.navigationDisabled) {
        return;
      }
      await apiClient.put(id + "/goto");
    },
    async putOpenBallot() {
      await apiClient.put(this.activeSlide.id + "/activate");
    },
    async putCancelCloseBallot() {
      await apiClient.put(this.activeSlide.id + "/cancel");
    },
    async putExtendCloseBallot(offset: number = 30) {
      await apiClient.put(this.activeSlide.id + "/extend", { time_offset: offset });
    },
    async putResetBallot() {
      await apiClient.put(this.activeSlide.id + "/reset");
    },
    async putCalculateResult(asPublic: boolean = true) {
      await apiClient.put(this.activeSlide.id + "/calculate", { publicised: asPublic });
    },
    async putMakeResultPublic() {
      await apiClient.put(this.activeSlide.id + "/make_public");
    },
    async putCancelBallot() {
      await apiClient.put(this.activeSlide.id + "/cancel");
    },
    async putStopLive() {
      await apiClient.put("stop_live");
    },
  }
});
</script>
